import { inject } from '@angular/core';
import { TRACKING_MODELS, TrackingService } from '@celerofinancas/service-utils';
import { UserStateModel } from '@celerofinancas/state-management';
import { CoreRegistryConfig } from '@celerofinancas/ui-interceptors';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { WebsocketService } from '@celerofinancas/ui-websocket';
import { TokenAuthenticationEvents } from '@shared/models/token-authentication-events';

/**
 * Factory function to create core registry configuration for this application
 *
 * This can be used as a reference for how to configure the generic interceptor
 * in other applications
 */
export function createCoreRegistryConfig(): CoreRegistryConfig {
  const tokens = inject(TokenHandlerService);
  const tracking = inject(TrackingService);
  const websocketService = inject(WebsocketService);

  return {
    coreRegistryUrlsWhiteList: ['economic_group/by', 'company/by/', 'me/read'],
    authWhiteList: [
      'auth/access_token',
      'auth/id_token_authentication',
      'auth/refresh_token',
      'auth/logout',
      'openid-configuration',
      'csrf-token',
    ],
    onError: () => {
      tokens.eventsSubject$.next({
        event: TokenAuthenticationEvents.TOKEN_EXCHANGED_FAILED,
      });
    },
    onInitialized: (user: UserStateModel) => {
      sessionRegister(user, tracking);

      if (tokens.accessToken) {
        websocketService.connect(tokens.accessToken);
      }
    },
  };
}

/**
 * Timezone used to compute the calendar day boundary for the session flag,
 * so the flag rolls over at midnight America/Sao_Paulo regardless of the
 * browser's local timezone.
 */
const SESSION_REGISTER_TIMEZONE = 'America/Sao_Paulo';

/**
 * Formats the current date as `YYYY-MM-DD` in `SESSION_REGISTER_TIMEZONE`.
 */
function currentDateKey(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: SESSION_REGISTER_TIMEZONE }).format(new Date());
}

/**
 * Register user session and tracking
 *
 * The flag is keyed by user and calendar day. A flag scoped only to the tab
 * (no user, no day) suppresses the event forever once set, even after
 * midnight or for a different user sharing the same tab.
 *
 * @param user Logged user
 * @param tracking Tracking service
 */
function sessionRegister(user: UserStateModel, tracking: TrackingService): void {
  const { pk, user_groups_cs } = user ?? {};
  const groups = user_groups_cs?.map(({ name }: any) => name);
  const sessionKey = `appStarted:${pk}:${currentDateKey()}`;

  if (!sessionStorage.getItem(sessionKey)) {
    tracking.track(TRACKING_MODELS.biPlatformAccess, { groups });
    sessionStorage.setItem(sessionKey, new Date().toISOString());
  }
}
